// src/components/common/SparkInput.tsx
import React from 'react';

/**
 * We avoid the “autoComplete type clash” error by
 * *not* intersecting several HTML attribute interfaces.
 * Instead we extend the generic HTMLElement attributes
 * and expose the few props our component actually needs.
 */
export interface SparkInputProps
  extends React.HTMLAttributes<HTMLElement> {
  /** Choose control type; 'textarea' & 'select' map to their elements, the rest to <input>. */
  type?: 'text' | 'email' | 'textarea' | 'password' | 'tel' | 'date' | 'time' | 'select';
  /** Controlled value – string for all supported controls. */
  value?: string;
  /** Change handler for input, textarea, or select. */
  onChange?: (e: React.ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  fullWidth?: boolean;
  options?: Array<{ value: string; label: string }>; // for “select”
  children?: React.ReactNode;                        // extra <option>s
  className?: string;
  required?: boolean
  disabled?: boolean;
  id?: string;
}

const SparkInput: React.FC<SparkInputProps> = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  error,
  fullWidth = true,
  options,
  children,
  className = '',
  disabled = false,
  id,
  ...rest
}) => {
  const inputClasses = [
    'spark-input',
    error ? 'spark-input--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const commonProps = {
    id,
    className: inputClasses,
    disabled,
    value,
    onChange,
    placeholder,
    ...rest,
  };

  const renderControl = () => {
    if (type === 'textarea') {
      return <textarea {...commonProps} />;
    }

    if (type === 'select') {
      return (
        <select {...commonProps}>
          {options?.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {children}
        </select>
      );
    }

    // Default: native input
    return <input {...commonProps} type={type} />;
  };

  return (
    <div className={`spark-field ${fullWidth ? 'spark-field--full-width' : ''}`}>
      {label && (
        <label htmlFor={id} className="spark-field__label">
          {label}
        </label>
      )}
      {renderControl()}
      {error && <div className="spark-field__error">{error}</div>}
    </div>
  );
};

export default SparkInput;
